// ProfessionalDetails.jsx
import React, { useEffect, useState } from 'react';

// Dependencies
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import useInput from '@digital_monorepo/lib-common/hooks/useInput';

// Components
import ProfessionalPhoto from '@/components/common/ProfessionalPhoto';
import Icon from '@/components/common/Icon';
import ProfessionalNotes from '@/components/data/professionals/ProfessionalNotes';
import ProfessionalSkills from '@/components/data/professionals/ProfessionalSkills';
import ProfessionalPTO from '@/components/data/professionals/ProfessionalPTO';
import ProfessionalManualAllocations from '@/components/data/professionals/ProfessionalManualAllocations';
import ProfessionalEngagements from '@/components/data/professionals/ProfessionalEngagements';
import ProfessionalForm from '@/components/data/professionals/ProfessionalForm';

// Services
import { updateProfessional, renameProfessional, deleteProfessional } from '@/services/schedulingService';

// Styles
import { professionaldetailsCss, professionalsCss, modalCss, buttonCss } from '@digital_monorepo/styling';

const ProfessionalDetails = ({ professional, engagements, startDates, onUpdate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isContractor = (professional.area || '').toLowerCase() === 'contractor';

  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [gender, setGender] = useState(professional.Gender || '');
  const [genderError, setGenderError] = useState(false);

  useEffect(() => {
    setGender(professional.Gender || '');
    setGenderError(false);
  }, [professional.ProfessionalID, professional.Gender]);

  // No import writes Gender, so unlike the fields in the contractor-only edit
  // form it is editable for everyone, straight from the strip.
  const handleGenderChange = async e => {
    const previous = gender;
    const value = e.target.value;
    setGender(value);
    setGenderError(false);
    try {
      await updateProfessional(professional.ProfessionalID, { Gender: value || null });
    } catch (err) {
      console.error('Error updating gender:', err);
      setGender(previous);
      setGenderError(true);
    }
  };

  // Only Name is required — contractors may legitimately have empty Function/Office/etc.
  const nameInput = useInput(v => !!v);
  const idInput = useInput(v => !!v); // only used for contractor ID rename
  const levelInput = useInput(() => true);
  const funcInput = useInput(() => true);
  const officeInput = useInput(() => true);
  const rcInput = useInput(() => true);
  const areaInput = useInput(() => true);

  const openEdit = () => {
    setErrorMessage('');
    idInput.valueChangeHandler({ target: { value: professional.ProfessionalID || '' } });
    nameInput.valueChangeHandler({ target: { value: professional.ProfessionalName || '' } });
    levelInput.valueChangeHandler({ target: { value: professional.FunctionLevel || '' } });
    funcInput.valueChangeHandler({ target: { value: professional.FunctionName || '' } });
    officeInput.valueChangeHandler({ target: { value: professional.Office || '' } });
    rcInput.valueChangeHandler({ target: { value: professional.ProfessionalRC || '' } });
    areaInput.valueChangeHandler({ target: { value: professional.area || '' } });
    setIsEditing(true);
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!nameInput.value || !nameInput.value.trim()) {
      setErrorMessage('Name is required.');
      nameInput.inputBlurHandler();
      return;
    }
    setSubmitting(true);
    setErrorMessage('');
    try {
      const newId = idInput.value?.trim();
      const oldId = professional.ProfessionalID;

      // If the ID changed, rename first (updates PK + all related tables).
      if (newId && newId !== oldId) {
        await renameProfessional(oldId, newId);
      }

      // Then update the other fields against the (potentially new) ID.
      await updateProfessional(newId && newId !== oldId ? newId : oldId, {
        ProfessionalName: nameInput.value,
        FunctionLevel: levelInput.value,
        FunctionName: funcInput.value,
        Office: officeInput.value,
        ProfessionalRC: rcInput.value,
        area: areaInput.value
      });
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating professional:', err);
      const apiMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || 'Failed to save changes. Please try again.';
      setErrorMessage(apiMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleteSubmitting(true);
    setDeleteError('');
    try {
      await deleteProfessional(professional.ProfessionalID);
      // The record this page is built on is gone, so go back to the list
      // rather than re-rendering a detail view of nothing.
      navigate('/professionals');
    } catch (err) {
      console.error('Error deleting professional:', err);
      setDeleteError(err?.message || 'Failed to delete professional. Please try again.');
      setDeleteSubmitting(false);
    }
  };

  const formData = {
    ProfessionalID: idInput.value,
    ProfessionalName: nameInput.value,
    FunctionLevel: levelInput.value,
    FunctionName: funcInput.value,
    Office: officeInput.value,
    ProfessionalRC: rcInput.value,
    area: areaInput.value,
    idError: idInput.hasError,
    nameError: nameInput.hasError,
    levelError: levelInput.hasError,
    functionError: funcInput.hasError,
    officeError: officeInput.hasError,
    rcError: rcInput.hasError,
    areaError: areaInput.hasError,
    onIdChange: idInput.valueChangeHandler,
    onNameChange: nameInput.valueChangeHandler,
    onLevelChange: levelInput.valueChangeHandler,
    onFunctionChange: funcInput.valueChangeHandler,
    onOfficeChange: officeInput.valueChangeHandler,
    onRcChange: rcInput.valueChangeHandler,
    onAreaChange: areaInput.valueChangeHandler,
    onIdBlur: idInput.inputBlurHandler,
    onNameBlur: nameInput.inputBlurHandler,
    onLevelBlur: levelInput.inputBlurHandler,
    onFunctionBlur: funcInput.inputBlurHandler,
    onOfficeBlur: officeInput.inputBlurHandler,
    onRcBlur: rcInput.inputBlurHandler,
    onAreaBlur: areaInput.inputBlurHandler
  };

  // Name, ID, function and office are already in the page header above, so the
  // strip carries only the facts that are not on show there.
  const metaFields = [
    { label: t('PROFESSIONALS_TABLE.HEADERS.FUNCTION_LEVEL'), value: professional.FunctionLevel },
    { label: t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_RC'), value: professional.ProfessionalRC },
    { label: t('PROFESSIONALS_TABLE.HEADERS.AREA'), value: professional.area }
  ];

  return (
    <div className={professionaldetailsCss.container}>
      <Link to='/professionals' className='workspace-back'>
        <Icon name='arrow' size={15} />
        All professionals
      </Link>
      {/* Page header — who am I looking at, and what can I do to them */}
      <div className={professionaldetailsCss.header}>
        <div className={professionaldetailsCss.identity}>
          <ProfessionalPhoto professional={professional} size={48} />
          <div className={professionaldetailsCss.identityText}>
            <h1 className={professionaldetailsCss.name}>{professional.ProfessionalName || 'Unnamed professional'}</h1>
            <div className={professionaldetailsCss.identitySub}>
              <span className={professionaldetailsCss.identityId}>{professional.ProfessionalID}</span>
              {professional.FunctionName && (
                <>
                  <span className={professionaldetailsCss.identitySep}>·</span>
                  {professional.FunctionName}
                </>
              )}
              {professional.Office && (
                <>
                  <span className={professionaldetailsCss.identitySep}>·</span>
                  {professional.Office}
                </>
              )}
              {professional.DpnUrl && !isContractor && (
                <>
                  <span className={professionaldetailsCss.identitySep}>·</span>
                  <a href={professional.DpnUrl} target='_blank' rel='noopener noreferrer'>
                    View DPN profile
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={professionaldetailsCss.headerActions}>
          {isContractor && (
            <button type='button' onClick={openEdit} className={`${professionaldetailsCss.headerBtn} ${professionaldetailsCss.headerBtnEdit}`}>
              Edit
            </button>
          )}
          <button
            type='button'
            onClick={() => {
              setDeleteError('');
              setIsDeleting(true);
            }}
            className={`${professionaldetailsCss.headerBtn} ${professionaldetailsCss.headerBtnDelete}`}>
            Delete
          </button>
        </div>
      </div>

      {/* Identity facts, inline instead of a tall mostly-empty card */}
      <div className={professionaldetailsCss.metaStrip}>
        {metaFields.map((field, index) => (
          <div key={index} className={professionaldetailsCss.metaItem}>
            <span className={professionaldetailsCss.metaLabel}>{field.label}</span>
            <span className={professionaldetailsCss.metaValue}>{field.value || '—'}</span>
          </div>
        ))}
        <div className={professionaldetailsCss.metaItem}>
          <label htmlFor='professional-gender' className={professionaldetailsCss.metaLabel}>
            Gender
          </label>
          <select id='professional-gender' className={professionaldetailsCss.metaSelect} value={gender} onChange={handleGenderChange}>
            <option value=''>Not specified</option>
            <option value='Female'>Female</option>
            <option value='Male'>Male</option>
          </select>
          {genderError && (
            <span className={professionaldetailsCss.metaError} role='alert'>
              Could not save
            </span>
          )}
        </div>
      </div>

      <div className={professionaldetailsCss.profileGrid}>
        {/* Notes & Skills Card */}
        <div className={professionaldetailsCss.profileCard}>
          <h3 className={professionaldetailsCss.cardTitle}>
            {t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_NOTES')} & {t('PROFESSIONALS_TABLE.HEADERS.SKILLS')}
          </h3>
          <div className={professionaldetailsCss.interactiveField}>
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', marginBottom: '0.25rem' }}>{t('PROFESSIONALS_TABLE.HEADERS.PROFESSIONAL_NOTES')}</div>
              <ProfessionalNotes professionalID={professional.ProfessionalID} professionalNotes={professional.ProfessionalNotes} />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', marginBottom: '0.25rem' }}>{t('PROFESSIONALS_TABLE.HEADERS.SKILLS')}</div>
              <ProfessionalSkills professionalID={professional.ProfessionalID} skills={professional.Skills} />
            </div>
          </div>
        </div>

        {/* PTO Card */}
        <div className={professionaldetailsCss.profileCard}>
          <h3 className={professionaldetailsCss.cardTitle}>{t('PROFESSIONALS_TABLE.HEADERS.PTO')}</h3>
          <div className={professionaldetailsCss.interactiveField}>
            <ProfessionalPTO professionalID={professional.ProfessionalID} pto={professional.PTO} onUpdate={onUpdate} />
          </div>
        </div>

        {/* Manual Allocations Card */}
        <div className={professionaldetailsCss.profileCard}>
          <h3 className={professionaldetailsCss.cardTitle}>Manual Allocations</h3>
          <div className={professionaldetailsCss.interactiveField}>
            <ProfessionalManualAllocations professionalID={professional.ProfessionalID} onUpdate={onUpdate} />
          </div>
        </div>
      </div>

      {/* Engagements Section */}
      {engagements && <ProfessionalEngagements engagements={engagements} startDates={startDates} />}

      {/* Edit Modal (contractors only) */}
      <Modal show={isEditing} onHide={() => setIsEditing(false)} centered dialogClassName={professionalsCss.editModal} className={`${modalCss['modal-overlay']}`}>
        <div className={modalCss['modal-content']}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Professional</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {errorMessage && (
              <div className='alert alert-danger' role='alert'>
                {errorMessage}
              </div>
            )}
            <ProfessionalForm
              formData={formData}
              onSubmit={handleSave}
              onCancel={() => setIsEditing(false)}
              formType='edit'
              isContractor={isContractor}
              isSubmitting={submitting}
              hasError={!!errorMessage}
            />
          </Modal.Body>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={isDeleting} onHide={() => setIsDeleting(false)} centered dialogClassName={professionalsCss.centeredModal} className={`${modalCss['modal-overlay']}`}>
        <div className={modalCss['modal-content']}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {deleteError && (
              <div className='alert alert-danger' role='alert'>
                {deleteError}
              </div>
            )}
            <p>
              Are you sure you want to delete professional: <strong>{professional.ProfessionalName}</strong>?
            </p>
            <p className='text-danger mt-2'>This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setIsDeleting(false)} disabled={deleteSubmitting} className={`${buttonCss.secondaryBtn} ${professionalsCss.adminButton}`}>
              Cancel
            </Button>
            <Button variant='danger' onClick={handleDelete} disabled={deleteSubmitting} className={`${buttonCss.primaryBtn} ${professionalsCss.adminButton}`}>
              {deleteSubmitting ? 'Deleting…' : 'Delete'}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default ProfessionalDetails;
